import { CONCURRENT_TASK_TYPES, ConcurrentTaskType, ExecutionTask, UITask } from './types';
import { selectQueuedTasksForType } from './managerUtils';
import type { Runtime } from './runtime';
import { logger } from '@/context/house/tasks/logging';
import type { TaskStatus } from '@/types/tasks';
import React from 'react';

export type ConcurrencyLimits = {
	maxConcurrent: number;
	serverSideActive: number;
	clientSideActive: number;
	totalLoad: number;
	availableSlots: number;
	canAcceptMore: boolean;
};

export type GetConcurrencyLimits = (taskType: ConcurrentTaskType) => ConcurrencyLimits;

export type Dispatchers = {
	updateStatus: (id: string, status: TaskStatus, progress?: number) => void;
};

export type ProcessQueuedTasksDeps = {
	getConcurrencyLimits: GetConcurrencyLimits;
	uiTasksRef: React.MutableRefObject<UITask[]>;
	globalTasksRef: React.MutableRefObject<Map<string, UITask>>; // parity with context
	processingRef: React.MutableRefObject<boolean>;
	runtime: Runtime;
	houseId: number;
	executeTask: (executionTask: ExecutionTask, signal: AbortSignal) => Promise<void>;
	dispatchers: Dispatchers;
};

/**
 * Pure task scheduler. Orchestrates queue draining per task type
 * respecting provided concurrency limits and runtime marks.
 *
 * No React imports beyond types; no side effects beyond injected callbacks.
 */
export function processQueuedTasks(deps: ProcessQueuedTasksDeps): void {
	const { getConcurrencyLimits, uiTasksRef, processingRef, runtime, houseId, executeTask, dispatchers } = deps;

	if (processingRef.current) {
		return;
	}

	processingRef.current = true;

	const logLimits = (taskType: ConcurrentTaskType) => {
		const limits = getConcurrencyLimits(taskType);
		logger.debug(
			`Concurrency limits [${taskType}] -> server: ${limits.serverSideActive}, client: ${limits.clientSideActive}, ` +
				`total: ${limits.totalLoad}/${limits.maxConcurrent}, slots: ${limits.availableSlots}, canAcceptMore: ${limits.canAcceptMore}`,
		);
	};
	logLimits(CONCURRENT_TASK_TYPES.UPLOAD_RECORD);
	logLimits(CONCURRENT_TASK_TYPES.DELETE_RECORD);
	logLimits(CONCURRENT_TASK_TYPES.UPDATE_RECORD);

	try {
		(Object.values(CONCURRENT_TASK_TYPES) as ConcurrentTaskType[]).forEach((taskType) => {
			const limits = getConcurrencyLimits(taskType);
			if (limits.availableSlots <= 0) {
				logger.info(
					`No available slots for [${taskType}]; totalLoad=${limits.totalLoad}/${limits.maxConcurrent}. Deferring.`,
				);
				return;
			}

			const queuedTasks = selectQueuedTasksForType({
				uiTasks: uiTasksRef.current,
				taskType,
				houseId,
				dispatched: runtime.marks.dispatched,
			});
			if (queuedTasks.length === 0) return;

			const tasksToExecute = queuedTasks.slice(0, limits.availableSlots);
			logger.info(
				`Dispatching ${tasksToExecute.length} [${taskType}] tasks (availableSlots=${limits.availableSlots})`,
			);

			for (const uiTask of tasksToExecute) {
				const now = Date.now();
				if (!runtime.canDispatch(uiTask.id, now, 50)) {
					logger.info(`Skip dispatch ${uiTask.id}: already executing or within debounce window`);
					continue;
				}
				const abortController = new AbortController();
				runtime.registerAbortController(uiTask.id, abortController);
				runtime.markDispatched(uiTask.id, now);
				runtime.markExecuting(uiTask.id);

				executeTask(uiTask.execution, abortController.signal)
					.catch((error) => {
						logger.error(`Unhandled error in executeTask for ${uiTask.id}:`, error);
						dispatchers.updateStatus(uiTask.id, 'failed');
					})
					.finally(() => {
						runtime.releaseAbortController(uiTask.id, abortController.signal);
						runtime.releaseExecuting(uiTask.id);
					});
			}
		});
	} finally {
		processingRef.current = false;
	}
}
